import { createBrowserRouter, Navigate } from 'react-router-dom';

import { AppShell } from './app-shell/AppShell.js';
import { AdminRoute } from './app-shell/AdminRoute.js';
import { NotFoundPage } from './app-shell/NotFoundPage.js';
import { ProtectedRoute } from './app-shell/ProtectedRoute.js';
import { PublicLayout } from './app-shell/PublicLayout.js';
import { PublicOnlyRoute } from './app-shell/PublicOnlyRoute.js';
import { AdminPage } from './features/admin/AdminPage.js';
import {
  ForgotPasswordPage,
  LoginPage,
  RegisterPage,
  ResetPasswordPage,
} from './features/auth/index.js';
import { DashboardPage } from './features/dashboard/DashboardPage.js';
import { FrameShowcasePage } from './features/dev/FrameShowcasePage.js';
import { FriendsPage } from './features/friends/FriendsPage.js';
import { CatalogPage, GameHostPage } from './features/games/index.js';
import { NotificationsPage } from './features/notifications/NotificationsPage.js';
import { OwnProfileRedirect } from './features/profile/OwnProfileRedirect.js';
import { ProfilePage } from './features/profile/ProfilePage.js';
import { SettingsPage } from './features/settings/SettingsPage.js';

/**
 * Route table (TECH_STACK §11). The catalog (`/games`) and the shared game
 * host (`/play/:sessionId`) are wired in Milestone 3; Friends, player
 * search, profiles, notifications, and settings' blocked-users list are
 * wired in Milestone 4 (docs/APP_PLAN.md §12 M4). History is wired in
 * Milestone 6 (docs/APP_PLAN.md §12 M6). Every route renders inside the
 * shared `AppShell` except the public auth screens, which use the lighter
 * `PublicLayout`.
 */
export const router = createBrowserRouter([
  {
    element: <PublicOnlyRoute />,
    children: [
      {
        element: <PublicLayout />,
        children: [
          { path: '/register', element: <RegisterPage /> },
          { path: '/login', element: <LoginPage /> },
          { path: '/forgot-password', element: <ForgotPasswordPage /> },
          { path: '/reset-password', element: <ResetPasswordPage /> },
        ],
      },
    ],
  },
  {
    element: <AppShell />,
    children: [
      { path: '/', element: <DashboardPage /> },
      ...(import.meta.env.DEV ? [{ path: '/__dev/frames', element: <FrameShowcasePage /> }] : []),
      {
        element: <ProtectedRoute />,
        children: [
          { path: '/games', element: <CatalogPage /> },
          { path: '/play/:sessionId', element: <GameHostPage /> },
          { path: '/friends', element: <FriendsPage /> },
          // The standalone Active Games page was removed in favor of the
          // "Continue Playing" sections on Home and Games, which already
          // show every in-progress session (approved Games-page mockup).
          // Keep a redirect so existing links/bookmarks to `/active-games`
          // resolve, matching the `/history` and `/invitations` pattern.
          { path: '/active-games', element: <Navigate to="/games" replace /> },
          // Game History moved under Settings (docs/UI_GUIDELINES.md §5).
          // Keep a redirect so existing links/bookmarks to `/history` resolve.
          { path: '/history', element: <Navigate to="/settings?tab=history" replace /> },
          // Game invitations are acted on under Notifications and no longer
          // have their own destination (APP_SPEC §20, §33). Same treatment
          // as `/history`: existing links and notification deep-links must
          // still resolve rather than hitting the 404 page.
          { path: '/invitations', element: <Navigate to="/notifications" replace /> },
          { path: '/notifications', element: <NotificationsPage /> },
          { path: '/profile', element: <OwnProfileRedirect /> },
          { path: '/settings', element: <SettingsPage /> },
          { path: '/users/:username', element: <ProfilePage /> },
          {
            element: <AdminRoute />,
            children: [{ path: '/admin', element: <AdminPage /> }],
          },
        ],
      },
      { path: '*', element: <NotFoundPage /> },
    ],
  },
]);
